import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import DeleteHostIcon from 'assets/icons/ico-delete-host.png'
import EditHostIcon from 'assets/icons/ico-edit-host.png'
import AirPlaneHostIcon from 'assets/icons/ico-airplane-host.png'
import { eventSelector, eventTokenSelector, isHostSelector } from 'src/selectors'
import PopConfirm from 'components/common/PopConfirm/PopConfirm'
import HobnobModal from 'components/common/HobnobModalV2/HobnobModal'
import Button from 'components/common/Button'
import SendUpdate from './SendUpdate/SendUpdate'
import { appUrl } from 'src/containers/time-poll/constants'
import { deleteEvent, downloadGuestList } from 'src/actions/event'
import { flyerDesignLayoutConfig } from 'src/assets/frontend-json/flyer-position'
import { routerActions } from 'react-router-redux'
import { changeVisible, setContent } from 'src/actions/globalLayer'
import { UPGRADE_PLAN_SOURCE } from 'src/constants'
import { LockOutlined } from '@ant-design/icons'
import cn from 'classnames'
import styles from './host-options.scss'
import { message } from 'antd'
import LoadingComponent from 'src/components/common/LoadingComponent/LoadingComponent'
import DownloadIcon from 'assets/icons/ico-download.png'
import dateFormat from 'dateformat'
import DownloadAppModal from 'src/components/common/DownloadAppModal/DownloadAppModal'

const HostOptions = () => {
  const dispatch = useDispatch()
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false)
  const [isDownloadVisible, setIsDownloadVisible] = useState(false)
  const [isSendUpdateVisible, setIsSendUpdateVisible] = useState(false)
  const [isDownloadGuestListLoading, setIsDownloadGuestListLoading] = useState(false)
  const [isDownloadInfoModalVisible, setIsDownloadInfoModalVisible] = useState(false)

  const event = useSelector(eventSelector)
  const eventToken = useSelector(eventTokenSelector)
  const isHost = useSelector(isHostSelector)

  const handleDeleteEvent = () => {
    dispatch(
      deleteEvent({
        eventId: event.id,
        onSuccessCallback: () => {
          location.href = '/e/list'
        }
      })
    )
  }

  const navigateToEdit = () => {
    const designNumbers = flyerDesignLayoutConfig
      .map(design => design.designIds)
      .reduce((a, b) => a.concat(b), [])

    const design_number = event.flyer?.design_number || event.pending_flyer?.design_number

    if (designNumbers.indexOf(Number(design_number)) > -1 || event.flyer?.design_number === 0) {
      dispatch(routerActions.push(`/e/edit/${eventToken}`))
    } else {
      setIsDownloadVisible(true)
    }
  }

  const handleClickSendUpdate = () => {
    if (event.grandfathered || event.feature_level >= 1) {
      setIsSendUpdateVisible(true)
      return
    }

    if (event.feature_level < 1) {
      dispatch(setContent(UPGRADE_PLAN_SOURCE.sendUpdate))
      dispatch(changeVisible(true))
    }
  }

  const downloadingGuestList = () => {
    if (event.state === 'draft') {
      setIsDownloadInfoModalVisible(true)
    } else {
      setIsDownloadGuestListLoading(true)
      dispatch(
        downloadGuestList({
          eventId: event.id,
          onSuccessCallback: data => {
            const blob = data instanceof Blob ? data : new Blob([data], { type: 'text/csv;charset=utf-8;' })
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            const filename = `${dateFormat(new Date(), 'yyyy-mm-dd')}_Guest_List.csv`
            link.setAttribute('download', filename)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            setIsDownloadGuestListLoading(false)
          },
          onErrorCallback: () => {
            message.error('Failed to download guest list')
            setIsDownloadGuestListLoading(false)
          }
        })
      )
    }
  }

  const icons = [
    <div
      className={cn(styles['icon-wrapper'], {
        [styles['icon-wrapper-hidden']]: event.state === 'draft'
      })}
      onClick={handleClickSendUpdate}
      key="send-update"
    >
      <img src={AirPlaneHostIcon} width="30" />
      <div className={styles['icon-label']}>
        Send Update{event.feature_level < 1 && <LockOutlined style={{ marginLeft: 5 }} />}
      </div>
    </div>,
    <div
      className={styles['icon-wrapper']}
      onClick={navigateToEdit}
      key="edit-event"
    >
      <img src={EditHostIcon} width="30" />
      <div className={styles['icon-label']}>Edit Event</div>
    </div>
  ]

  if (isHost) {
    icons.push(
      <div
        className={cn(styles['icon-wrapper'], {
          [styles['icon-wrapper-disabled']]: event.state === 'draft'
        })}
        onClick={downloadingGuestList}
        key="download-guest-list"
      >
        <img src={DownloadIcon} width="30" />
        <div className={styles['icon-label']}>Download Guest List</div>
      </div>
    )
  }

  if (isHost) {
    icons.push(
      <div
        className={styles['icon-wrapper']}
        onClick={() => setIsRemoveModalVisible(true)}
        key="delete-event"
      >
        <img src={DeleteHostIcon} width="30" />
        <div style={{ color: '#d0021b' }} className={styles['icon-label']}>
          delete event
        </div>
      </div>
    )
  }

  const remainder = icons.length % 2
  if (remainder !== 0) {
    icons.push(
      <div
        className={styles['icon-wrapper']}
        style={{ visibility: 'hidden' }}
        key="empty"
      />
    )
  }

  return (
    <div className={styles['host-option-container']}>
      <h3 className={styles['host-option-title']}>host options</h3>
      <div className={styles['icon-container']}>
        {icons}
      </div>

      {
        isDownloadGuestListLoading && (
          <div className={styles['loading-component']}>
            <LoadingComponent light />
          </div>
        )
      }

      <PopConfirm
        visible={isRemoveModalVisible}
        confirmText="DELETE"
        type="delete"
        onClose={() => setIsRemoveModalVisible(false)}
        onConfirm={handleDeleteEvent}
      >
        Are you sure you want to delete this event?
        <br /> This action cannot be undone.
      </PopConfirm>

      <SendUpdate
        isSendUpdateVisible={isSendUpdateVisible}
        onSetIsSendUpdateVisible={setIsSendUpdateVisible}
      />

      <DownloadAppModal
        show={isDownloadVisible}
        onClose={() => setIsDownloadVisible(false)}
        title="Your Event was created on the app, go back to Hobnob app to edit it."
        showAppleIcon
        buttonText="Open in App"
        href={appUrl}
        buttonClassName={styles['button-inner']}
      />

      <HobnobModal
        visible={isDownloadInfoModalVisible}
        onCancel={() => setIsDownloadInfoModalVisible(false)}
      >
        <div className={styles['download-info-container']}>
          <div className={styles['download-info-title']}>
            Download Guest List
          </div>
          <div className={styles['download-info-text']}>
            Downloading Guest List will be enabled once your event is published.
          </div>
          <Button key="ok" type="primary" className={styles['download-info-button']} onClick={() => setIsDownloadInfoModalVisible(false)}>
            OK
          </Button>
        </div>
      </HobnobModal>

    </div>
  )
}

export default HostOptions
